/* Reusable Page behavior. Commerce uses actual variant IDs, allocation-backed
 * purchase options and native forms. The helper preserves cart-updated and
 * toggle-cart events; deployed app/cart behavior still requires browser QA. */
(function () {
  'use strict';
  // Loaded once per page even though every campaign section emits the script tag:
  // the namespaced guard below makes additional executions no-ops.
  if (window.__spmShilajitLandingPage) return;
  window.__spmShilajitLandingPage = true;

  var ROOT_SELECTOR = '[data-spm-shilajit-landing-page]';
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Actual-variant purchase forms are also the no-JS fallback. Each native
   * form owns its own valid selling_plan options; no option tuple inference. */
  function initCommerce(root, cleanups) {
    root.querySelectorAll('[data-spm-commerce]').forEach(function (commerce) {
      initCommerceInstance(root, commerce, cleanups);
    });
  }
  function initCommerceInstance(root, commerce, cleanups) {
    // Keep the native forms visible when enhancement capabilities are absent.
    if (!window.fetch || !window.FormData) return;
    var imageRoot = commerce.closest('[data-spm-buy-card]') || root;
    var choices = Array.prototype.slice.call(commerce.querySelectorAll('[data-spm-variant-choice]'));
    var panes = Array.prototype.slice.call(commerce.querySelectorAll('[data-spm-variant-pane]'));
    // An incomplete app/editor render must leave the native forms alone and
    // must not interrupt initialization of another card or section on the Page.
    if (!panes.length || panes.some(function (pane) {
      var form = pane.querySelector('[data-spm-purchase-form]');
      return !form || !form.querySelector('[data-spm-atc]') || !form.querySelector('[data-spm-error]');
    })) return;
    var pending = false;
    var selectedId = commerce.getAttribute('data-default-variant');
    var quantity = '1';
    var selectedPlan = null;
    var explicitPlan = null;
    var requestedTrigger = null;
    var active = true;
    var remoteFeedback = [];

    function listen(node, type, callback) {
      node.addEventListener(type, callback);
      cleanups.push(function () { node.removeEventListener(type, callback); });
    }
    function selectedOption(select) {
      return select && select.options[select.selectedIndex];
    }
    function feedbackFor(trigger) {
      var entry = remoteFeedback.filter(function (item) { return item.trigger === trigger; })[0];
      if (!entry) {
        var status = trigger.ownerDocument.createElement('p');
        status.setAttribute('data-spm-purchase-status', '');
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        trigger.insertAdjacentElement('afterend', status);
        entry = { trigger: trigger, status: status, busy: trigger.getAttribute('aria-busy'), waiting: false };
        remoteFeedback.push(entry);
      }
      return entry;
    }
    function feedback(trigger, message, waiting) {
      var entry = feedbackFor(trigger);
      entry.status.textContent = message;
      entry.waiting = !!waiting;
      if (waiting) trigger.setAttribute('aria-busy', 'true');
    }
    function restoreTrigger(entry) {
      if (entry.busy === null) entry.trigger.removeAttribute('aria-busy');
      else entry.trigger.setAttribute('aria-busy', entry.busy);
      entry.waiting = false;
    }
    function finishFeedback(message) {
      remoteFeedback.forEach(function (entry) {
        if (!entry.waiting) return;
        entry.status.textContent = message;
        restoreTrigger(entry);
      });
    }
    function revealForm(form, control) {
      var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      form.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      if (control) control.focus({ preventScroll: true });
    }
    function updateForm(form) {
      var selector = form.querySelector('[data-spm-plan]');
      var option = selectedOption(selector);
      // Also preserve omission if the browser falls back to a native submit.
      if (selector) {
        if (option && option.value) selector.setAttribute('name', 'selling_plan');
        else selector.removeAttribute('name');
      }
      var price = form.querySelector('[data-spm-price]');
      var compare = form.querySelector('[data-spm-compare]');
      var button = form.querySelector('[data-spm-atc]');
      var text = form.querySelector('[data-spm-atc-text]');
      var available = form.getAttribute('data-available') === 'true';
      if (selector && (!option || (selector.required && !option.value))) available = false;
      if (price && option) price.textContent = option.getAttribute('data-price') || '';
      if (compare && option) {
        compare.textContent = option.getAttribute('data-compare') || '';
        compare.hidden = !compare.textContent;
      }
      if (price && compare) price.classList.toggle('spm-price--sale', !compare.hidden);
      if (button) button.disabled = pending || !available;
      if (text) {
        text.textContent = available ? text.getAttribute('data-prefix') : text.getAttribute('data-soldout-label');
        if (available && text.getAttribute('data-mode') === 'landing') {
          text.textContent += ' — ' + text.getAttribute('data-variant-label') + ' · ' + (price ? price.textContent : '');
        }
      }
      form.querySelectorAll('[data-spm-plan-detail]').forEach(function (detail) {
        detail.hidden = !option || detail.getAttribute('data-spm-plan-detail') !== option.value;
      });
    }
    function selectVariant(id, preserveSelection) {
      var target = panes.filter(function (pane) { return pane.getAttribute('data-variant-id') === id; })[0];
      if (!target) return;
      selectedId = id;
      panes.forEach(function (pane) { pane.hidden = pane !== target; });
      choices.forEach(function (choice) {
        choice.checked = choice.value === id;
        var label = choice.closest('[data-spm-choice-label]');
        if (label) label.classList.toggle('on', choice.checked);
      });
      var form = target.querySelector('[data-spm-purchase-form]');
      var qty = form.querySelector('[data-spm-qty]');
      if (qty) qty.value = quantity;
      var plan = form.querySelector('[data-spm-plan]');
      var planChange = form.querySelector('[data-spm-plan-change]');
      if (planChange) planChange.hidden = true;
      // Carry an explicit plan choice only when allocated to this variant.
      if (preserveSelection && plan && selectedPlan !== null) {
        var match = Array.prototype.slice.call(plan.options).some(function (option) { return option.value === selectedPlan; });
        plan.selectedIndex = match ? Array.prototype.findIndex.call(plan.options, function (option) { return option.value === selectedPlan; }) : 0;
        if (!match && selectedPlan && planChange) planChange.hidden = false;
      } else if (preserveSelection && !plan && selectedPlan && planChange) {
        planChange.hidden = false;
      }
      selectedPlan = plan ? plan.value : '';
      if (!preserveSelection || explicitPlan !== selectedPlan) explicitPlan = null;
      updateForm(form);
      var image = imageRoot.querySelector('[data-spm-variant-image]');
      if (image && target.getAttribute('data-image')) {
        image.src = target.getAttribute('data-image');
        image.alt = image.getAttribute('data-spm-image-alt') || target.getAttribute('data-image-alt') || '';
        image.removeAttribute('srcset');
      }
    }

    panes.forEach(function (pane) {
      var form = pane.querySelector('[data-spm-purchase-form]');
      var plan = form.querySelector('[data-spm-plan]');
      var qty = form.querySelector('[data-spm-qty]');
      var error = form.querySelector('[data-spm-error]');
      var button = form.querySelector('[data-spm-atc]');
      if (plan) listen(plan, 'change', function () {
        selectedPlan = plan.value;
        explicitPlan = plan.value;
        var notice = form.querySelector('[data-spm-plan-change]');
        if (notice) notice.hidden = true;
        updateForm(form);
      });
      if (qty) listen(qty, 'input', function () { quantity = qty.value; });
      [['[data-spm-qty-minus]', -1], ['[data-spm-qty-plus]', 1]].forEach(function (pair) {
        var stepper = form.querySelector(pair[0]);
        if (!stepper || !qty) return;
        stepper.hidden = false;
        listen(stepper, 'click', function () {
          var value = parseInt(qty.value, 10);
          qty.value = String(Math.max(1, (isNaN(value) ? 1 : value) + pair[1]));
          quantity = qty.value;
        });
      });
      form.querySelectorAll('[data-spm-live-price]').forEach(function (price) { price.hidden = false; });
      updateForm(form);

      // Native action remains usable if fetch/FormData are missing.
      if (!window.fetch || !window.FormData) return;
      listen(form, 'submit', function (event) {
        var cartAddUrl = root.getAttribute('data-cart-add-url') || form.getAttribute('action');
        if (!cartAddUrl) return;
        event.preventDefault();
        if (pending || button.disabled || !form.reportValidity()) return;
        pending = true;
        if (requestedTrigger) feedback(requestedTrigger, 'Adding to cart…', true);
        error.hidden = true;
        error.textContent = '';
        button.setAttribute('aria-busy', 'true');
        panes.forEach(function (item) { updateForm(item.querySelector('[data-spm-purchase-form]')); });
        var request;
        try {
          var body = new FormData(form);
          // Shopify accepts an omitted selling_plan for one-time purchases.
          if (body.get('selling_plan') === '') body.delete('selling_plan');
          var url = cartAddUrl.replace(/\.js$/, '') + '.js';
          request = fetch(url, {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: body
          });
        } catch (failure) { request = Promise.reject(failure); }
        Promise.resolve(request).then(function (response) {
          return response.json().then(function (payload) {
            if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to add to cart. Please try again.');
            return payload;
          });
        }).then(function () {
          if (active) finishFeedback('Added to cart.');
          window.dispatchEvent(new CustomEvent('cart-updated'));
          window.dispatchEvent(new CustomEvent('toggle-cart'));
        }).catch(function (failure) {
          if (active) {
            error.textContent = failure && failure.message ? failure.message : 'Unable to add to cart. Please try again.';
            error.hidden = false;
            finishFeedback(error.textContent);
          }
        }).then(function () {
          pending = false;
          if (active) {
            button.removeAttribute('aria-busy');
            panes.forEach(function (item) { updateForm(item.querySelector('[data-spm-purchase-form]')); });
          }
        });
      });
    });
    choices.forEach(function (choice) {
      listen(choice, 'change', function () { if (choice.checked && !choice.disabled) selectVariant(choice.value, true); });
    });
    listen(commerce, 'spm:purchase-request', function (event) {
      var trigger = event.detail && event.detail.trigger;
      var pane = panes.filter(function (item) { return item.getAttribute('data-variant-id') === selectedId; })[0];
      var form = pane && pane.querySelector('[data-spm-purchase-form]');
      // Leave the caller's product-link fallback intact without a usable bridge.
      if (!trigger || !trigger.ownerDocument || !form || typeof form.requestSubmit !== 'function' ||
          !(root.getAttribute('data-cart-add-url') || form.getAttribute('action'))) return;
      event.preventDefault();
      if (pending) { feedback(trigger, 'Adding to cart…', true); return; }
      feedback(trigger, '', false);
      updateForm(form);
      var button = form.querySelector('[data-spm-atc]');
      var plan = form.querySelector('[data-spm-plan]');
      if (button.disabled) { feedback(trigger, 'This purchase option is unavailable.', false); return; }
      // A remote CTA must never silently enroll a required subscription.
      if (plan && plan.required && (!explicitPlan || explicitPlan !== plan.value)) {
        feedback(trigger, 'Choose a purchase option before adding to cart.', false);
        revealForm(form, plan);
        return;
      }
      if (!form.reportValidity()) {
        feedback(trigger, 'Review the quantity and purchase options before adding to cart.', false);
        revealForm(form, form.querySelector('[data-spm-qty]') || plan || button);
        return;
      }
      requestedTrigger = trigger;
      try {
        form.requestSubmit(button);
        if (!pending) feedback(trigger, 'Review the purchase options before adding to cart.', false);
      } catch (failure) {
        feedback(trigger, 'Unable to add to cart. Please try again.', false);
      } finally { requestedTrigger = null; }
    });
    // No form is hidden until every form has been initialized successfully.
    commerce.classList.add('spm-commerce--enhanced');
    commerce.querySelectorAll('[data-spm-native-forms]').forEach(function (node) { node.hidden = true; });
    commerce.querySelectorAll('[data-spm-enhanced-form]').forEach(function (node) { node.hidden = false; });
    var chooser = commerce.querySelector('[data-spm-choices]');
    if (chooser) chooser.hidden = choices.length < 2;
    if (!panes.some(function (pane) { return pane.getAttribute('data-variant-id') === selectedId; })) selectedId = panes[0].getAttribute('data-variant-id');
    selectVariant(selectedId, false);
    cleanups.push(function () {
      active = false;
      remoteFeedback.forEach(function (entry) { restoreTrigger(entry); entry.status.remove(); });
      remoteFeedback = [];
      panes.forEach(function (pane) { pane.querySelector('[data-spm-atc]').removeAttribute('aria-busy'); });
      commerce.classList.remove('spm-commerce--enhanced');
      commerce.querySelectorAll('[data-spm-native-forms]').forEach(function (node) { node.hidden = false; });
      commerce.querySelectorAll('[data-spm-enhanced-form]').forEach(function (node) { node.hidden = true; });
      if (chooser) chooser.hidden = true;
      panes.forEach(function (pane) { pane.hidden = false; });
    });
  }


  function initSection(root) {
    if (!root || root.__spmInit) return;
    root.__spmInit = true;
    var cleanups = [];
    root.__spmCleanups = cleanups;

    /* Navigation is handled by the shared, duplicate-aware anchor controller. */

    /* Buy module */
    initCommerce(root, cleanups);
  }

  function destroySection(root) {
    if (!root || !root.__spmInit) return;
    (root.__spmCleanups || []).forEach(function (cleanup) {
      try { cleanup(); } catch (err) { /* noop */ }
    });
    root.__spmCleanups = [];
    root.__spmInit = false;
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); }, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector(ROOT_SELECTOR);
    if (root) { destroySection(root); }
  });
})();
